/* globals BoostModel */

import Context from "shared/util/Context";
import ClientStats from "shared/util/ClientStats";
import GlobalMessaging from "features/global_messaging";
import Navigation from "legacy/Navigation";
import EventNotification from "shared/util/EventNotification";
import getImagePath from "shared/util/getImagePath";
import getImageResizerPath from "shared/util/getImageResizerPath";

const isAlist = Context.get("isAlist");
const imagePath = getImagePath();
const resizerImagePath = getImageResizerPath();

// ========================================================================================
// Event Popup controller
// ----------------------------------------------------------------------------------------

const EventUiManager = {
	// number of seconds between instant event messages
	m_time_between_messages: 30,
	m_time_between_duplicates: 60 * 5,

	m_should_wait: true,
	m_annoyance_count: 0,
	m_wait_timer: null,
	m_message_queue: [],
	m_recent_history: {}, // a hash: keys are usernames, values are arrays of types shown
	m_recent_history_clears: {},

	m_destination_div: false,
	m_optional_params: {},

	m_event_num: 0,
	m_event_count: 0,
	m_event_max: 3,

	m_event_display_time: 12000,

	notifications: [],

	invoke(destinationDiv, optionalParams) {
		this.m_destination_div = destinationDiv;

		if (optionalParams) {
			this.m_optional_params = optionalParams;
		}
	},

	update(person, event, info) {
		const hisHer = person.gender === "M" ? "his" : "her";

		// abort if it's a message notification, and you have the global messager
		// open with that person
		if (event.type === "msg_notify") {
			if (GlobalMessaging.isConversationVisible(person.userid)) {
				return false;
			}
		}

		// kick out spotlight users
		if (event.type === "new_spotlight_user") {
			return false;
		}

		// if no info (type and action and stuff), get it
		let updatedInfo = info;

		if (typeof (updatedInfo) === "undefined") {
			updatedInfo = this.getType(person, event, hisHer);
		}

		const usernameLowerCased = person.screenname.toLowerCase();
		const recentHistory = this.m_recent_history[usernameLowerCased];

		const isDupeEvent = recentHistory && recentHistory.indexOf(event.type) !== -1;
		const isDupePerson = recentHistory && !isDupeEvent;

		const element = document.getElementById(`notification_${event.type}_${person.screenname || person.displayname}`);

		if (element) {
			updatedInfo.skip_event = true;
		}

		if (typeof (BoostModel) !== "undefined") {
			const state = BoostModel.get("state");

			if (state === "running" || state === "reporting") {
				const validtypes = ["msg_notify", "looks_vote", "stalk"];

				if (validtypes.indexOf(event.type) !== -1) {
					updatedInfo.skip_event = true;
				}
			}
		}

		if (updatedInfo.skip_event || isDupeEvent) {
			this.updateNextInQueue();
			return false;
		}

		// if everything's all clear for popup to appear
		if (!this.shouldWait(person, event) || isDupePerson && this.m_should_wait) {
			this.makeEventPopup(person, event, updatedInfo);
			return true;
		}

		// if not, get in line, buddy
		this.m_message_queue.push({
			"person": person,
			"event": event,
			"info": updatedInfo,
		});

		return true;
	},

	// throttling
	// -------------------------------

	updateNextInQueue() {
		if (!this.m_message_queue.length || this.shouldWait()) { return; }

		// call an update on the first in the queue
		const item = this.m_message_queue[0];
		this.update(item.person, item.event, item.info);
		this.m_message_queue.splice(0, 1);
	},

	shouldWait() {
		return this.m_should_wait && this.m_annoyance_count > 0;
	},

	pauseMessages() {
		this.m_annoyance_count += 1;

		if (this.m_wait_timer) {
			clearTimeout(this.m_wait_timer);
		}

		this.m_wait_timer = setTimeout(
			this.startMessages.bind(this),
			1000 * this.m_time_between_messages,
		);
	},

	startMessages() {
		this.m_annoyance_count = 0;
		this.updateNextInQueue();
		this.m_recent_history = {};
	},

	// update DOM
	// -------------------------------

	getThumbUrl(thumb) {
		if (thumb && thumb.length && thumb.length > 0) {
			return `${resizerImagePath}/82x82/82x82/${thumb}`;
		} else {
			return `${imagePath}media/img/user/placeholder_2013/pq_82.png`;
		}
	},

	hideLowest() {
		// later notifications are plopped on the end.
		// therefore, traverse the array and hide the latest one.
		let done = false;
		this.notifications.forEach((notification) => {
			if (!done && notification.shown) {
				notification.close();
				done = true;
			}
		});
	},

	// blur out picture and name for nonalisters
	_maskProfileDetails(person) {
		const maskedDetails = { ...person };

		const randomNum = Math.floor(Math.random() * 24) + 1;

		if (maskedDetails.gender === 1) {
			maskedDetails.thumbnail = `${imagePath}media/img/user/blurred/M/60-${randomNum}.jpg`;
		} else {
			maskedDetails.thumbnail = `${imagePath}media/img/user/blurred/F/60-${randomNum}.jpg`;
		}

		maskedDetails.displayname = "Someone";
		maskedDetails.link = "/who-likes-you";

		return maskedDetails;
	},

	makeEventPopup(person, event, info) {
		// perform action
		if (info.action && typeof info.action === "function") {
			info.action(person, event);
		}

		// bail before notification if it's a message, we have global messaging, and
		// the message thread is in the overflow panel
		if (event.type === "msg_notify") {
			if (GlobalMessaging.isConversationOverflowed(person.userid)) {
				return;
			}
		}

		// at this point, throw out matches which are not alist, unless spotlight is on
		let booststate;

		if (typeof (BoostModel) !== "undefined") {
			booststate = BoostModel.get("state");
		}

		if (event.type === "looks_vote" && booststate !== "running" && !isAlist) {
			return;
		}

		let updatedDetails = { ...person };

		// if they're not alist and it's not a mutual match, strip user info
		if (!isAlist && event.type !== "mutual_match") {
			updatedDetails = this._maskProfileDetails(person);
		}

		// throw stats
		if (info.stat) {
			ClientStats.update(info.stat);
		}

		const notification = new EventNotification({
			"person": updatedDetails,
			"eventtype": event.type,
			"title": info.title,
			"infostring": info.string,
			"link": event.link,
		});

		this.notifications.push(notification);

		if (this.m_event_count >= this.m_event_max && booststate !== "running" && booststate !== "reporting") {
			this.hideLowest();
		}

		this.m_event_count += 1;
		this.m_event_num += 1;

		// tell imfooter that we have notifications
		const element = document.getElementById("im_footer");

		if (element) {
			element.classList.add("hasnotifications");
		}

		const usernameLowerCased = person.screenname.toLowerCase();

		// put the [jerk] in recent history so we know to bring his notifications up
		this.m_recent_history[usernameLowerCased] = this.m_recent_history[usernameLowerCased] || [];

		this.m_recent_history[usernameLowerCased].push(event.type);

		// if a boost is on, skip this last part
		if (booststate === "running" || booststate === "reporting") {
			return;
		}

		// clear recent history after a bit
		if (this.m_recent_history_clears[usernameLowerCased]) {
			clearTimeout(this.m_recent_history_clears[usernameLowerCased]);
		}

		this.m_recent_history_clears[usernameLowerCased] = setTimeout(() => {
			this.m_recent_history[usernameLowerCased] = [];
		}, this.m_time_between_duplicates * 1000);
	},

	mouseover() {
		this.notifications.forEach((e) => {
			if (e.close_timer) {
				clearTimeout(e.close_timer);
			}
		});
	},

	mouseout() {
		this.notifications.forEach((e) => {
			e.closeSoon();
		});
	},

	getType(person, event, hisHer) {
		const types = {
			"msg_notify": {
				text: "sent you a message!",
				stat: "instant events - orbit notice - sent you a message",

				action() {
					Navigation.increment("mailbox");
				},
			},
			"stalk": {
				text: "visited your profile!",
				stat: "instant events - orbit notice - you've been stalked",

				action() {
					Navigation.increment("visitors");
				},
			},
			"looks_vote": {
				text: "likes you!",
				stat: "instant events - orbit notice - rated you",

				action() {
					Navigation.increment("ratings");
				},
			},
			"mutual_match": {
				text: "you like each other!",
				stat: "instant events - orbit notice - mutual match",

				action() {
					Navigation.increment("mailbox");
				},
			},
			"orbit_profile_updated": {
				text: `edited ${hisHer} profile!`,
				stat: "instant events - orbit notice - edited profile",
			},
			"orbit_nth_question": {
				text: `answered ${event.event_arg1} questions!`,
				stat: "instant events - orbit notice - nth question",
			},
			"orbit_picture_upload": {
				text: "uploaded a new photo!",
				stat: "instant events - orbit notice - photo upload",
			},
			"orbit_user_signon": {
				text: "signed in!",
				stat: "instant events - orbit notice - logged in",
			},
			"orbit_user_signoff": {
				text: "signed out.",
				stat: "instant events - orbit notice - logged out",
			},
		};
		const type = types[event.type];

		let response;

		if (!type) {
			response = {
				"skip_event": true,
			};
		} else {
			response = {
				"string": type.text,
				"skip_event": type.skip_event,
				"action": type.action,
			};
		}

		if (type && type.stat && type.hash) {
			response.stat = type.stat;
			response.hash = type.hash;
		}

		return response;
	},

	// clear extra notifications
	// -------------------------------

	addSpotlightNotification(id, person) {
		let updatedDetails = { ...person };

		if (!isAlist && person.ietype !== "mutual_match") {
			updatedDetails = this._maskProfileDetails(person);
		}

		const notification = new EventNotification({
			"id": id,
			"person": updatedDetails,
			"eventtype": person.ietype,
			"infostring": person.info,
		});

		this.notifications.push(notification);

		const usernameLowerCased = person.username.toLowerCase();

		// put the [jerk] in recent history so we know to bring his notifications up
		this.m_recent_history[usernameLowerCased] = this.m_recent_history[usernameLowerCased] || [];
		this.m_recent_history[usernameLowerCased].push(person.ietype);

		this.m_event_count += 1;
		this.m_event_num += 1;
	},

	clearNotifications() {
		const eventDisplay = document.getElementById("event_display");
		eventDisplay.classList.add("no_transition");

		this.notifications.forEach((notification) => notification.close(true));
		setTimeout(() => {
			eventDisplay.classList.remove("no_transition");
		}, 250);
	},
};

window.EventUiManager = EventUiManager;
export default EventUiManager;



// WEBPACK FOOTER //
// ./src/shared/util/EventUiManager.js